import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, max, min } from '@angular/forms/signals'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { firstValueFrom, Subscription } from 'rxjs'
import { KirjanpitajanNimitiedot } from '../../../_jaettu-lemonator/model/kirjanpitaja'
import { LlmKategoriaprofiiliPromptRow, LlmTestitapausKategoria, TilikarttaRaja } from '../../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'
import { luoPromptiversioVertailu, PromptiversioVertailu, VertailtavaPromptiversio } from '../prompti-versiovertailu.component'

interface RajanKentat {
  alkaa: FieldTree<number | null>
  loppuu: FieldTree<number | null>
  tilaus: Subscription
}

@Component({
  selector: 'app-kategoria-promptti',
  templateUrl: './kategoria-promptti.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KategoriaPromptiComponent implements OnInit, OnChanges {
  @Input() kategoria!: LlmTestitapausKategoria
  @Input() uusin: LlmKategoriaprofiiliPromptRow | undefined
  @Input() muokattu = ''
  @Input() lataa = false
  @Input() tallennetaan = false
  @Input() historia: LlmKategoriaprofiiliPromptRow[] = []
  @Input() naytaHistoria = false
  @Input() esikatselu: LlmKategoriaprofiiliPromptRow | null = null
  @Input() tilikarttaRajat: TilikarttaRaja[] = []

  @Output() muokattuChange = new EventEmitter<string>()
  @Output() tallenna = new EventEmitter<void>()
  @Output() toggleHistoria = new EventEmitter<void>()
  @Output() esikatsele = new EventEmitter<LlmKategoriaprofiiliPromptRow>()
  @Output() suljeEsikatselu = new EventEmitter<void>()
  @Output() palautaVersio = new EventEmitter<number>()
  @Output() tilikarttaRajatChange = new EventEmitter<TilikarttaRaja[]>()

  esikatseluVertailu: PromptiversioVertailu | null = null

  muokattuKentta = form(signal(''))
  rajaKentat: RajanKentat[] = []

  private readonly _kirjanpitajatMap = signal<Map<string, KirjanpitajanNimitiedot>>(new Map())
  private readonly _injector = inject(Injector)

  constructor(private _kirjanpitajaService: KirjanpitajaService) {
    arvonMuutokset(this.muokattuKentta).subscribe(arvo => this.muokattuChange.emit(arvo))
  }

  async ngOnInit() {
    this._kirjanpitajatMap.set(await firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable))
  }

  ngOnChanges(muutokset: SimpleChanges) {
    if (muutokset['muokattu']) {
      asetaArvo(this.muokattuKentta, this.muokattu ?? '', { ilmoita: false })
    }
    if (muutokset['tilikarttaRajat']) {
      this._paivitaRajaKentat()
    }
    if (!muutokset['esikatselu'] && !muutokset['historia'] && !muutokset['uusin']) {
      return
    }
    const muunna = (rivi: LlmKategoriaprofiiliPromptRow): VertailtavaPromptiversio => ({
      versio: rivi.versio,
      paivitetty: rivi.paivitetty,
      teksti: rivi.prompt,
      tilikarttaRajat: rivi.tilikarttaRajat,
    })
    this.esikatseluVertailu = this.esikatselu ? luoPromptiversioVertailu(this.esikatselu, this.historia, this.uusin, muunna) : null
  }

  annaPaivittajanNimi(uid: string | null | undefined): string {
    if (!uid) {
      return '—'
    }
    const k = this._kirjanpitajatMap().get(uid)
    return k ? `${k.etunimi} ${k.sukunimi}` : uid
  }

  get rajatVirheellisia(): boolean {
    return this.tilikarttaRajat.some((_, i) => this.rajaVirheellinen(i))
  }

  rajaVirheellinen(indeksi: number): boolean {
    const raja = this.tilikarttaRajat[indeksi]
    if (!raja) {
      return true
    }
    if (!Number.isInteger(raja.alkaa) || !Number.isInteger(raja.loppuu)) {
      return true
    }
    if (raja.alkaa < 0 || raja.alkaa > 9999 || raja.loppuu < 0 || raja.loppuu > 9999) {
      return true
    }
    return raja.alkaa > raja.loppuu
  }

  lisaaRaja() {
    this.tilikarttaRajatChange.emit([...this.tilikarttaRajat, { alkaa: 0, loppuu: 0 }])
  }

  poistaRaja(indeksi: number) {
    this.tilikarttaRajatChange.emit(this.tilikarttaRajat.filter((_, i) => i !== indeksi))
  }

  paivitaAlkaa(indeksi: number, arvo: unknown) {
    this._paivitaRaja(indeksi, { alkaa: this._numero(arvo) })
  }

  paivitaLoppuu(indeksi: number, arvo: unknown) {
    this._paivitaRaja(indeksi, { loppuu: this._numero(arvo) })
  }

  private _paivitaRaja(indeksi: number, muutos: Partial<TilikarttaRaja>) {
    const paivitetyt = this.tilikarttaRajat.map((raja, i) => (i === indeksi ? { ...raja, ...muutos } : raja))
    this.tilikarttaRajatChange.emit(paivitetyt)
  }

  private _paivitaRajaKentat() {
    const rajat = this.tilikarttaRajat ?? []
    while (this.rajaKentat.length > rajat.length) {
      this.rajaKentat.pop().tilaus.unsubscribe()
    }
    rajat.forEach((raja, indeksi) => {
      const kentat = this.rajaKentat[indeksi]
      if (kentat) {
        asetaArvo(kentat.alkaa, raja.alkaa ?? null, { ilmoita: false })
        asetaArvo(kentat.loppuu, raja.loppuu ?? null, { ilmoita: false })
        return
      }
      const alkaa = this._luoRajakentta(raja.alkaa ?? null)
      const loppuu = this._luoRajakentta(raja.loppuu ?? null)
      const tilaus = arvonMuutokset(alkaa, { injector: this._injector }).subscribe(arvo => this.paivitaAlkaa(indeksi, arvo))
      tilaus.add(arvonMuutokset(loppuu, { injector: this._injector }).subscribe(arvo => this.paivitaLoppuu(indeksi, arvo)))
      this.rajaKentat.push({ alkaa, loppuu, tilaus })
    })
  }

  private _luoRajakentta(arvo: number | null): FieldTree<number | null> {
    return form(
      signal<number | null>(arvo),
      polku => {
        min(polku, 0)
        max(polku, 9999)
      },
      { injector: this._injector },
    )
  }

  private _numero(arvo: unknown): number {
    if (arvo === null || arvo === undefined || arvo === '') {
      return 0
    }
    const n = Number(arvo)
    return Number.isFinite(n) ? n : 0
  }
}
